import { useState, useEffect } from "react";
import {
	useLoaderData,
	useNavigate,
	useRouteLoaderData,
	useSearchParams,
	useRevalidator,
	useLocation,
	useNavigation,
} from "react-router";
import { supabase } from "@shared/integrations/supabase/client";
import { useAuthenticatedUser } from "@/hooks/useAuthenticatedUser";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@/components/ui/dialog";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";

import { toast } from "sonner";
import {
	ArrowLeft,
	Plus,
	Upload,
	Trash2,
	Lock,
	Unlock,
	Edit2,
	Check,
	X,
	Globe,
	EyeOff,
	ChevronDown,
} from "lucide-react";
import { Switch } from "@/components/ui/switch";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { WorkspaceSidebar } from "@/components/WorkspaceSidebar";
import { CreateDatasetDialog } from "@/components/CreateDatasetDialog";
import { CreateDerivativeDialog } from "@/components/CreateDerivativeDialog";
import { Badge } from "@/components/ui/badge";
import { PGFlowStatusBadge } from "@/components/shared/PGFlowStatusBadge";
import type { Database } from "@/integrations/supabase/types";
import {
	WORKSPACE_LOADER_ID,
	type WorkspaceLoaderData,
} from "@/routes/workspaceLoader";
import { useWorkspaceSidebarActions } from "@/hooks/useWorkspaceSidebarActions";
import { datasetDetailLoader } from "@/routes/datasetDetailLoader";

type DatasetTestRun = Database["public"]["Tables"]["test_runs"]["Row"] & {
	projects?: { title: string | null } | undefined;
	pgflow_status?: string | null;
	pgflow_remaining_steps?: number | null;
	pgflow_latest_step_slug?: string | null;
	pgflow_latest_step_status?: string | null;
	pgflow_last_error?: string | null;
	pgflow_active_worker_id?: string | null;
};

interface ParsedCSVEntry {
	input: string;
	expected_output: string;
	parent_id?: string;
	rule_text?: string;
}

export default function DatasetDetail() {
	const navigate = useNavigate();
	const location = useLocation();
	const user = useAuthenticatedUser();
	const {
		dataset,
		entries,
		totalPages,
		page,
		derivatives: derivativeDatasets,
		parentEntries: parentDatasetEntries,
		testRuns: loaderTestRuns,
		lockState,
	} = useLoaderData<typeof datasetDetailLoader>();
	const testRuns = loaderTestRuns as DatasetTestRun[];
	const { projects, codes, datasets } = useRouteLoaderData(
		WORKSPACE_LOADER_ID,
	) as WorkspaceLoaderData;
	const [searchParams, setSearchParams] = useSearchParams();
	const revalidator = useRevalidator();
	const navigation = useNavigation();
	const [createDatasetDialogOpen, setCreateDatasetDialogOpen] = useState(false);
	const {
		createProject,
		createCode,
		createJudge,
		createDataset: submitCreateDataset,
		updateProjectTitle,
		updateCodeTitle,
		updateJudgeTitle,
	} = useWorkspaceSidebarActions();

	const handleCreateDataset = (
		name: string,
		datasetKind: "behavior_examples" | "operational_rules",
	) => {
		submitCreateDataset({ name, dataset_kind: datasetKind });
	};

	const [isLocked, setIsLocked] = useState(lockState.isLocked);
	const [lockReason, setLockReason] = useState<string>(lockState.lockReason);
	const [isEditing, setIsEditing] = useState(false);
	const [editedName, setEditedName] = useState(dataset.name);

	// Dialogs
	const [showAddDialog, setShowAddDialog] = useState(false);
	const [showDeleteDialog, setShowDeleteDialog] = useState(false);
	const [showDeleteDatasetDialog, setShowDeleteDatasetDialog] = useState(false);
	const [showBulkImportDialog, setShowBulkImportDialog] = useState(false);
	const [showMappingDialog, setShowMappingDialog] = useState(false);
	const [deleteEntryId, setDeleteEntryId] = useState<string | null>(null);

	// Form data
	const [newInput, setNewInput] = useState("");
	const [newOutput, setNewOutput] = useState("");
	const [selectedParentEntryId, setSelectedParentEntryId] =
		useState<string>("");
	const [parentEntries, setParentEntries] = useState<
		Array<{ id: string; input: string; rule_text: string | null }>
	>([]);
	const [csvData, setCsvData] = useState("");
	const [parsedEntries, setParsedEntries] = useState<ParsedCSVEntry[]>([]);

	// Adversarial dialog state
	const [showDerivativeDialog, setShowDerivativeDialog] = useState(false);

	useEffect(() => {
		setIsLocked(lockState.isLocked);
		setLockReason(lockState.lockReason);
	}, [lockState.isLocked, lockState.lockReason]);

	useEffect(() => {
		setEditedName(dataset.name);
	}, [dataset.id, dataset.name]);

	const currentPage = page;
	const handlePageChange = (nextPage: number) => {
		if (nextPage < 1 || nextPage > totalPages) return;
		const next = new URLSearchParams(searchParams);
		next.set("page", String(nextPage));
		setSearchParams(next);
	};

	const navigationTargetsCurrentPage =
		navigation.state !== "idle" &&
		navigation.location?.pathname === location.pathname;
	const isEntriesLoading =
		navigationTargetsCurrentPage || revalidator.state !== "idle";

	const handleToggleLock = () => {
		if (isLocked) {
			toast.error(`Cannot unlock: ${lockReason}`);
			return;
		}
		setIsLocked(!isLocked);
	};

	const handleTogglePublic = async () => {
		// If trying to make private, check if dataset is locked
		if (dataset.is_public && isLocked) {
			toast.error(
				"Cannot make dataset private: it is locked because it has adversarial datasets or has been used in test runs",
			);
			return;
		}

		// If trying to make public and this is an adversarial dataset, check parent
		if (!dataset.is_public && dataset.parent_id) {
			try {
				const { data: parentData, error: parentError } = await supabase
					.from("test_data_sets")
					.select("is_public")
					.eq("id", dataset.parent_id)
					.maybeSingle();

				if (parentError) throw parentError;

					if (!parentData?.is_public) {
						toast.error(
							"Cannot make adversarial dataset public: parent dataset is private",
						);
						return;
				}
			} catch (error) {
				console.error("Error checking parent dataset:", error);
				toast.error("Failed to verify parent dataset status");
				return;
			}
		}

		try {
			const { error } = await supabase
				.from("test_data_sets")
				.update({ is_public: !dataset.is_public })
				.eq("id", dataset.id);

			if (error) throw error;

			toast.success(
				`Dataset is now ${!dataset.is_public ? "public" : "private"}`,
			);
			revalidator.revalidate();
		} catch (error) {
			console.error("Error toggling public status:", error);
			toast.error("Failed to update dataset visibility");
		}
	};

	const handleNameEdit = async () => {
		if (!isEditing) {
			setIsEditing(true);
			return;
		}

		if (!editedName.trim()) {
			toast.error("Dataset name cannot be empty");
			return;
		}

		try {
			const { error } = await supabase
				.from("test_data_sets")
				.update({ name: editedName })
				.eq("id", dataset.id);

			if (error) throw error;

			setIsEditing(false);
			toast.success("Dataset name updated");
			revalidator.revalidate();
		} catch (error) {
			console.error("Error updating dataset name:", error);
			toast.error("Failed to update dataset name");
		}
	};

	const handleAddEntry = async () => {
		// Validation based on dataset kind
		if (dataset.dataset_kind === "operational_rules") {
			if (!newInput.trim() || !newOutput.trim()) {
				toast.error("Both rule text and expected output are required");
				return;
			}
		} else {
			if (!newInput.trim() || !newOutput.trim()) {
				toast.error("Both input and expected output are required");
				return;
			}
		}

		// Validate parent entry selection for adversarial datasets
		if (dataset.type === "adversarial" && !selectedParentEntryId) {
			toast.error("Please select a parent entry");
			return;
		}

		if (isLocked) {
			toast.error("This dataset is locked and cannot be modified");
			return;
		}

		if (!dataset) return;

		try {
			const entryData =
				dataset.dataset_kind === "operational_rules"
					? {
							test_data_set_id: dataset.id,
							input: "",
							expected_output: newOutput,
							rule_text: newInput,
							parent_id:
								dataset.type === "adversarial" ? selectedParentEntryId : null,
						}
					: {
							test_data_set_id: dataset.id,
							input: newInput,
							expected_output: newOutput,
							parent_id:
								dataset.type === "adversarial" ? selectedParentEntryId : null,
						};

			const { error } = await supabase
				.from("test_data_entries")
				.insert(entryData);

			if (error) throw error;

			toast.success("Test entry added successfully");
			setNewInput("");
			setNewOutput("");
			setSelectedParentEntryId("");
			setShowAddDialog(false);
			revalidator.revalidate();
		} catch (error) {
			console.error("Error adding test entry:", error);
			toast.error("Failed to add test entry");
		}
	};

	// Fetch parent entries when opening add dialog for adversarial datasets
	useEffect(() => {
		const fetchParentEntries = async () => {
			if (
				showAddDialog &&
				dataset?.type === "adversarial" &&
				dataset.parent_id
			) {
				const { data, error } = await supabase
					.from("test_data_entries")
					.select("id, input, rule_text")
					.eq("test_data_set_id", dataset.parent_id)
					.order("created_at", { ascending: true });

				if (!error && data) {
					setParentEntries(data);
				}
			}
		};

		fetchParentEntries();
	}, [showAddDialog, dataset]);

	const handleDeleteEntry = async () => {
		if (!deleteEntryId) return;

		if (isLocked) {
			toast.error("This dataset is locked and cannot be modified");
			return;
		}

		try {
			const { error } = await supabase
				.from("test_data_entries")
				.delete()
				.eq("id", deleteEntryId);

			if (error) throw error;

			toast.success("Test entry deleted");
			setDeleteEntryId(null);
			setShowDeleteDialog(false);
			revalidator.revalidate();
		} catch (error) {
			console.error("Error deleting test entry:", error);
			toast.error("Failed to delete test entry");
		}
	};

	const parseCSVLine = (line: string): string[] => {
		const result: string[] = [];
		let current = "";
		let inQuotes = false;

		for (let i = 0; i < line.length; i++) {
			const char = line[i];
			if (char === '"') {
				if (inQuotes && line[i + 1] === '"') {
					current += '"';
					i++;
				} else {
					inQuotes = !inQuotes;
				}
			} else if (char === "," && !inQuotes) {
				result.push(current);
				current = "";
			} else {
				current += char;
			}
		}
		result.push(current);
		return result;
	};

	const handleBulkImport = async () => {
		const currentDatasetId = dataset.id;

		if (!csvData.trim()) {
			toast.error("Please paste CSV data");
			return;
		}

		const lines = csvData.trim().split("\n");

		// Smart header detection: check if first line contains common header keywords
		const detectHeaderLine = (firstLine: string): boolean => {
			const lowerLine = firstLine.toLowerCase();
			const headerKeywords = ["input", "output", "expected", "rule"];
			return headerKeywords.some((keyword) => lowerLine.includes(keyword));
		};

		const startLine = detectHeaderLine(lines[0]) ? 1 : 0;

		const parsed: ParsedCSVEntry[] = [];

		if (dataset.dataset_kind === "operational_rules") {
			// For operational rules: each line has rule_text,expected_output
			for (let i = startLine; i < lines.length; i++) {
				const fields = parseCSVLine(lines[i]);
				if (fields.length >= 2) {
					const ruleText = fields[0].trim();
					const expectedOutput = fields[1].trim();
					if (ruleText && expectedOutput) {
						parsed.push({
							input: "", // Not used for operational rules
							expected_output: expectedOutput,
							rule_text: ruleText,
						});
					}
				}
			}
		} else {
			// For behavior examples: CSV with input, expected_output columns
			for (let i = startLine; i < lines.length; i++) {
				const fields = parseCSVLine(lines[i]);
				if (fields.length >= 2) {
					parsed.push({
						input: fields[0].trim(),
						expected_output: fields[1].trim(),
					});
				}
			}
		}

		if (dataset.type === "adversarial") {
			setParsedEntries(parsed);
			setShowBulkImportDialog(false);
			setShowMappingDialog(true);
		} else {
			try {
				const entriesToInsert: Database["public"]["Tables"]["test_data_entries"]["Insert"][] =
					parsed.map((entry) => ({
						test_data_set_id: currentDatasetId,
						input: entry.input || "",
						expected_output: entry.expected_output || "",
						rule_text: entry.rule_text || null,
					}));

				const { error } = await supabase
					.from("test_data_entries")
					.insert(entriesToInsert);

				if (error) throw error;

				toast.success(`Successfully imported ${parsed.length} entries`);
				setCsvData("");
				setShowBulkImportDialog(false);
				revalidator.revalidate();
			} catch (error) {
				console.error("Error importing CSV:", error);
				toast.error("Failed to import CSV data");
			}
		}
	};

	const handleSaveMappedEntries = async () => {
		const currentDatasetId = dataset.id;

		try {
			const entriesToInsert: Database["public"]["Tables"]["test_data_entries"]["Insert"][] =
				parsedEntries.map((entry) => ({
					test_data_set_id: currentDatasetId,
					input: entry.input || "",
					expected_output: entry.expected_output || "",
					parent_id: entry.parent_id || null,
					rule_text: entry.rule_text || null,
				}));

			const { error } = await supabase
				.from("test_data_entries")
				.insert(entriesToInsert);

			if (error) throw error;

			toast.success(`Successfully imported ${parsedEntries.length} entries`);
			setParsedEntries([]);
			setCsvData("");
			setShowMappingDialog(false);
			revalidator.revalidate();
		} catch (error) {
			console.error("Error saving mapped entries:", error);
			toast.error("Failed to save mapped entries");
		}
	};

	const updateParentMapping = (index: number, parentId: string) => {
		const updated = [...parsedEntries];
		updated[index].parent_id = parentId;
		setParsedEntries(updated);
	};

	const handleDeleteDataset = async () => {
		if (isLocked) {
			toast.error(`Cannot delete dataset: ${lockReason}`);
			setShowDeleteDatasetDialog(false);
			return;
		}

		try {
			const { error } = await supabase
				.from("test_data_sets")
				.delete()
				.eq("id", dataset.id);

			if (error) throw error;

			toast.success("Dataset deleted");
			navigate("/datasets");
		} catch (error) {
			console.error("Error deleting dataset:", error);
			toast.error("Failed to delete dataset");
		}
	};
	return (
		<SidebarProvider>
			<div className="min-h-screen flex w-full">
				<WorkspaceSidebar
					projects={projects}
					codes={codes}
					datasets={datasets}
					onNewProject={createProject}
					onNewCode={createCode}
					onNewJudge={createJudge}
					onNewDataset={() => setCreateDatasetDialogOpen(true)}
					onUpdateProjectTitle={updateProjectTitle}
					onUpdateCodeTitle={updateCodeTitle}
					onUpdateJudgeTitle={updateJudgeTitle}
					onOpenSettings={() => {}}
				/>
				<main className="flex-1 bg-background p-6 overflow-auto">
					<div className="w-full">
						<div className="flex items-center gap-2 mb-4">
							<SidebarTrigger />
							<Button variant="ghost" onClick={() => navigate("/datasets")}>
								<ArrowLeft className="mr-2 h-4 w-4" />
								Back to Datasets
							</Button>
							{dataset.type === "adversarial" && dataset.parent_id && (
								<Button
									variant="ghost"
									onClick={() => navigate(`/dataset/${dataset.parent_id}`)}
								>
									<ArrowLeft className="mr-2 h-4 w-4" />
									Parent Dataset
								</Button>
							)}
						</div>

						<Card>
							<CardHeader>
								<div className="flex items-center justify-between">
									<div className="flex items-center gap-2 flex-1">
										{isEditing ? (
											<>
												<Input
													value={editedName}
													onChange={(e) => setEditedName(e.target.value)}
													className="max-w-md"
												/>
												<Button
													size="icon"
													variant="ghost"
													onClick={handleNameEdit}
												>
													<Check className="h-4 w-4" />
												</Button>
												<Button
													size="icon"
													variant="ghost"
													onClick={() => {
														setIsEditing(false);
														setEditedName(dataset.name);
													}}
												>
													<X className="h-4 w-4" />
												</Button>
											</>
										) : (
											<>
												<CardTitle>{dataset.name}</CardTitle>
												<Button
													size="icon"
													variant="ghost"
													onClick={handleNameEdit}
												>
													<Edit2 className="h-4 w-4" />
												</Button>
											</>
										)}
									</div>
									<div className="flex items-center gap-2">
										{dataset.user_id === user?.id && (
											<div className="flex items-center gap-2 mr-4">
												<Label
													htmlFor="public-toggle"
													className="flex items-center gap-2 cursor-pointer"
												>
													{dataset.is_public ? (
														<Globe className="h-4 w-4" />
													) : (
														<EyeOff className="h-4 w-4" />
													)}
													<span className="text-sm">
														{dataset.is_public ? "Public" : "Private"}
													</span>
												</Label>
												<Switch
													id="public-toggle"
													checked={dataset.is_public}
													onCheckedChange={handleTogglePublic}
												/>
											</div>
										)}
										<Button
											size="icon"
											variant="ghost"
											onClick={handleToggleLock}
											disabled={isLocked}
											title={isLocked ? lockReason : "Lock/Unlock dataset"}
										>
											{isLocked ? (
												<Lock className="h-4 w-4" />
											) : (
												<Unlock className="h-4 w-4" />
											)}
										</Button>
									</div>
								</div>
								<CardDescription className="flex items-center gap-2 flex-wrap">
									<span>Type: {dataset.type}</span>
									<span>•</span>
									<Badge
										variant={
											dataset.dataset_kind === "behavior_examples"
												? "default"
												: "secondary"
										}
										className="text-xs"
									>
										{dataset.dataset_kind === "behavior_examples"
											? "Behaviors"
											: "Rules"}
									</Badge>
									<span>•</span>
									<span>
										Created: {new Date(dataset.created_at).toLocaleDateString()}
									</span>
									{isLocked && (
										<>
											<span>•</span>
											<span>Locked: {lockReason}</span>
										</>
									)}
								</CardDescription>
							</CardHeader>
							<CardContent>
								<div className="flex gap-2 mb-4 flex-wrap">
									<Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
										<DialogTrigger asChild>
											<Button disabled={isLocked}>
												<Plus className="mr-2 h-4 w-4" />
												Add Entry
											</Button>
										</DialogTrigger>
										<DialogContent>
											<DialogHeader>
												<DialogTitle>Add Test Entry</DialogTitle>
												<DialogDescription>
													Add a new test data entry to this dataset.
												</DialogDescription>
											</DialogHeader>
											<div className="space-y-4">
												{dataset.type === "adversarial" && (
													<div>
														<Label htmlFor="parent-entry">Parent Entry</Label>
														<Select
															value={selectedParentEntryId}
															onValueChange={setSelectedParentEntryId}
														>
															<SelectTrigger id="parent-entry">
																<SelectValue placeholder="Select parent entry" />
															</SelectTrigger>
															<SelectContent>
																{parentEntries.map((entry) => (
																	<SelectItem key={entry.id} value={entry.id}>
																		{entry.rule_text ||
																			entry.input?.substring(0, 50) ||
																			"Unnamed entry"}
																		{((entry.rule_text &&
																			entry.rule_text.length > 50) ||
																			(entry.input &&
																				entry.input.length > 50)) &&
																			"..."}
																	</SelectItem>
																))}
															</SelectContent>
														</Select>
													</div>
												)}
												{dataset.dataset_kind === "operational_rules" ? (
													<>
														<div>
															<Label htmlFor="rule-text">Rule Text</Label>
															<Textarea
																id="rule-text"
																value={newInput}
																onChange={(e) => setNewInput(e.target.value)}
																placeholder="Enter the rule..."
																rows={4}
															/>
														</div>
														<div>
															<Label htmlFor="output">Expected Output</Label>
															<Textarea
																id="output"
																value={newOutput}
																onChange={(e) => setNewOutput(e.target.value)}
																placeholder="Enter expected output..."
																rows={3}
															/>
														</div>
													</>
												) : (
													<>
														<div>
															<Label htmlFor="input">Input</Label>
															<Textarea
																id="input"
																value={newInput}
																onChange={(e) => setNewInput(e.target.value)}
																placeholder="Enter test input..."
																rows={3}
															/>
														</div>
														<div>
															<Label htmlFor="output">Expected Output</Label>
															<Textarea
																id="output"
																value={newOutput}
																onChange={(e) => setNewOutput(e.target.value)}
																placeholder="Enter expected output..."
																rows={3}
															/>
														</div>
													</>
												)}
												<Button onClick={handleAddEntry} className="w-full">
													Add Entry
												</Button>
											</div>
										</DialogContent>
									</Dialog>

									<Dialog
										open={showBulkImportDialog}
										onOpenChange={setShowBulkImportDialog}
									>
										<DialogTrigger asChild>
											<Button variant="outline" disabled={isLocked}>
												<Upload className="mr-2 h-4 w-4" />
												Bulk Import CSV
											</Button>
										</DialogTrigger>
										<DialogContent className="max-w-2xl">
											<DialogHeader>
												<DialogTitle>Bulk Import from CSV</DialogTitle>
												<DialogDescription>
													{dataset.dataset_kind === "operational_rules" ? (
														<>
															Paste CSV data with two columns: rule_text,
															expected_output
															<br />
															Example: "The agent should never offer discounts
															over 20%","Declines discount request"
														</>
													) : (
														"Paste CSV data with columns: input, expected_output"
													)}
												</DialogDescription>
											</DialogHeader>
											<Textarea
												value={csvData}
												onChange={(e) => setCsvData(e.target.value)}
												placeholder={
													dataset.dataset_kind === "operational_rules"
														? 'rule_text,expected_output\n"The agent should never offer discounts over 20%","Declines discount request"\n"The agent must verify user identity","Asks for verification"'
														: "input,expected_output\ntest input 1,test output 1\ntest input 2,test output 2"
												}
												rows={10}
												className="font-mono text-sm"
											/>
											<Button onClick={handleBulkImport}>Import</Button>
										</DialogContent>
									</Dialog>

									{dataset.type === "primary" && (
										<Button
											variant="outline"
											onClick={() => setShowDerivativeDialog(true)}
										>
											<Plus className="mr-2 h-4 w-4" />
											Create Adversarial
											<ChevronDown className="ml-2 h-4 w-4" />
										</Button>
									)}

									<Button
										variant="destructive"
										onClick={() => setShowDeleteDatasetDialog(true)}
									>
										<Trash2 className="mr-2 h-4 w-4" />
										Delete Dataset
									</Button>
								</div>

								{isEntriesLoading ? (
									<div className="text-center py-8 text-muted-foreground">
										Loading entries...
									</div>
								) : entries && entries.length > 0 ? (
									<>
										<Table>
											<TableHeader>
												<TableRow>
													{dataset.dataset_kind === "operational_rules" ? (
														<TableHead>Rule Text</TableHead>
													) : (
														<TableHead>Input</TableHead>
													)}
													<TableHead>Expected Output</TableHead>
													<TableHead>Created</TableHead>
													<TableHead className="w-20">Actions</TableHead>
												</TableRow>
											</TableHeader>
											<TableBody>
												{entries.map((entry) => (
													<TableRow key={entry.id}>
														<TableCell className="max-w-md truncate">
															{dataset.dataset_kind === "operational_rules"
																? entry.rule_text || "N/A"
																: entry.input}
														</TableCell>
														<TableCell className="max-w-md truncate">
															{entry.expected_output}
														</TableCell>
														<TableCell>
															{new Date(entry.created_at).toLocaleDateString()}
														</TableCell>
														<TableCell>
															<Button
																size="icon"
																variant="ghost"
																onClick={() => {
																	setDeleteEntryId(entry.id);
																	setShowDeleteDialog(true);
																}}
																disabled={isLocked}
															>
																<Trash2 className="h-4 w-4" />
															</Button>
														</TableCell>
													</TableRow>
												))}
											</TableBody>
										</Table>

										{totalPages > 1 && (
											<div className="flex justify-center gap-2 mt-4">
												<Button
													variant="outline"
													onClick={() =>
														handlePageChange(Math.max(1, currentPage - 1))
													}
													disabled={currentPage === 1}
												>
													Previous
												</Button>
												<span className="flex items-center px-4">
													Page {currentPage} of {totalPages}
												</span>
												<Button
													variant="outline"
													onClick={() =>
														handlePageChange(
															Math.min(totalPages, currentPage + 1),
														)
													}
													disabled={currentPage === totalPages}
												>
													Next
												</Button>
											</div>
										)}
									</>
								) : (
									<div className="text-center py-8 text-muted-foreground">
										No test entries yet. Add your first entry to get started.
									</div>
								)}
							</CardContent>
						</Card>

						{/* Adversarial Datasets */}
						{derivativeDatasets.length > 0 && (
							<Card className="mt-6">
								<CardHeader>
									<CardTitle>Adversarial Datasets</CardTitle>
									<CardDescription>
										Datasets created from this dataset
									</CardDescription>
								</CardHeader>
								<CardContent>
									<div className="space-y-2">
										{derivativeDatasets.map((derivative) => (
											<Button
												key={derivative.id}
												variant="outline"
												className="w-full justify-start"
												onClick={() => navigate(`/dataset/${derivative.id}`)}
											>
												<span className="font-medium">{derivative.name}</span>
												<span className="ml-auto text-sm text-muted-foreground">
													{new Date(derivative.created_at).toLocaleDateString()}
												</span>
											</Button>
										))}
									</div>
								</CardContent>
							</Card>
						)}

						{/* Usage Log */}
						{testRuns.length > 0 && (
							<Card className="mt-6">
								<CardHeader>
									<CardTitle>Usage Log</CardTitle>
									<CardDescription>
										Projects and test runs that have used this dataset
									</CardDescription>
								</CardHeader>
								<CardContent>
									<Table data-testid="dataset-usage-log-table">
										<TableHeader>
											<TableRow>
												<TableHead>Project</TableHead>
												<TableHead>Test Run Type</TableHead>
												<TableHead>Status</TableHead>
												<TableHead>Flow Status</TableHead>
												<TableHead>Date</TableHead>
											</TableRow>
										</TableHeader>
										<TableBody>
											{testRuns.map((run) => (
												<TableRow key={run.id}>
													<TableCell>
														{run.projects ? (
															<Button
																variant="link"
																className="p-0 h-auto font-normal"
																onClick={(e) => {
																	e.stopPropagation();
																	navigate(
																		`/workspace?project=${run.project_id}`,
																	);
																}}
															>
																{run.projects.title}
															</Button>
														) : (
															<span className="text-muted-foreground">
																Unknown Project
															</span>
														)}
													</TableCell>
													<TableCell className="capitalize">
														{run.type}
													</TableCell>
													<TableCell className="capitalize">
														{run.status}
													</TableCell>
													<TableCell>
														<div className="flex flex-col gap-1">
															<PGFlowStatusBadge
																status={run.pgflow_status ?? null}
															/>
															{run.pgflow_latest_step_slug ? (
																<span
																	className="text-xs text-muted-foreground"
																	data-testid="dataset-pgflow-latest-step"
																>
																	Latest:{" "}
																	{run.pgflow_latest_step_slug.replace(
																		/_/g,
																		" ",
																	)}
																	{run.pgflow_latest_step_status
																		? ` (${run.pgflow_latest_step_status})`
																		: ""}
																</span>
															) : null}
															{run.pgflow_last_error ? (
																<span
																	className="text-xs text-destructive"
																	data-testid="dataset-pgflow-last-error"
																>
																	Error: {run.pgflow_last_error}
																</span>
															) : null}
														</div>
													</TableCell>
													<TableCell>
														{new Date(run.created_at).toLocaleString()}
													</TableCell>
												</TableRow>
											))}
										</TableBody>
									</Table>
								</CardContent>
							</Card>
						)}

						<AlertDialog
							open={showDeleteDialog}
							onOpenChange={setShowDeleteDialog}
						>
							<AlertDialogContent>
								<AlertDialogHeader>
									<AlertDialogTitle>Delete Test Entry</AlertDialogTitle>
									<AlertDialogDescription>
										Are you sure you want to delete this test entry? This action
										cannot be undone.
									</AlertDialogDescription>
								</AlertDialogHeader>
								<AlertDialogFooter>
									<AlertDialogCancel onClick={() => setDeleteEntryId(null)}>
										Cancel
									</AlertDialogCancel>
									<AlertDialogAction onClick={handleDeleteEntry}>
										Delete
									</AlertDialogAction>
								</AlertDialogFooter>
							</AlertDialogContent>
						</AlertDialog>

						<Dialog
							open={showMappingDialog}
							onOpenChange={setShowMappingDialog}
						>
							<DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
								<DialogHeader>
									<DialogTitle>Map Entries to Parent</DialogTitle>
									<DialogDescription>
										Select the parent entry for each imported test case.
									</DialogDescription>
								</DialogHeader>
								<Table>
									<TableHeader>
										<TableRow>
											{dataset.dataset_kind === "operational_rules" ? (
												<TableHead>Rule Text</TableHead>
											) : (
												<>
													<TableHead>Input</TableHead>
													<TableHead>Expected Output</TableHead>
												</>
											)}
											<TableHead>Parent Entry</TableHead>
										</TableRow>
									</TableHeader>
									<TableBody>
										{parsedEntries.map((entry, index) => (
											<TableRow key={index}>
												{dataset.dataset_kind === "operational_rules" ? (
													<TableCell className="max-w-xs truncate">
														{entry.rule_text}
													</TableCell>
												) : (
													<>
														<TableCell className="max-w-xs truncate">
															{entry.input}
														</TableCell>
														<TableCell className="max-w-xs truncate">
															{entry.expected_output}
														</TableCell>
													</>
												)}
												<TableCell>
													<Select
														value={entry.parent_id || ""}
														onValueChange={(value) =>
															updateParentMapping(index, value)
														}
													>
														<SelectTrigger>
															<SelectValue placeholder="Select parent entry" />
														</SelectTrigger>
														<SelectContent>
															{parentDatasetEntries.map((parent) => {
																// Determine label based on parent dataset kind (from loader)
																const parentDataset = dataset.parent_id
																	? datasets.find(
																			(d) => d.id === dataset.parent_id,
																		)
																	: null;
																const parentDatasetKind =
																	parentDataset?.dataset_kind;
																const label =
																	parentDatasetKind === "operational_rules"
																		? parent.rule_text?.substring(0, 50) ||
																			"N/A"
																		: parent.input?.substring(0, 50) || "N/A";

																return (
																	<SelectItem key={parent.id} value={parent.id}>
																		{label}...
																	</SelectItem>
																);
															})}
														</SelectContent>
													</Select>
												</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
								<Button onClick={handleSaveMappedEntries} className="w-full">
									Save All Entries
								</Button>
							</DialogContent>
						</Dialog>

						<AlertDialog
							open={showDeleteDatasetDialog}
							onOpenChange={setShowDeleteDatasetDialog}
						>
							<AlertDialogContent>
								<AlertDialogHeader>
									<AlertDialogTitle>Delete Dataset</AlertDialogTitle>
									<AlertDialogDescription>
										Are you sure you want to delete this dataset? This action
										cannot be undone and will permanently remove all associated
										test data entries.
									</AlertDialogDescription>
								</AlertDialogHeader>
								<AlertDialogFooter>
									<AlertDialogCancel
										onClick={() => setShowDeleteDatasetDialog(false)}
									>
										Cancel
									</AlertDialogCancel>
									<AlertDialogAction onClick={handleDeleteDataset}>
										Delete
									</AlertDialogAction>
								</AlertDialogFooter>
							</AlertDialogContent>
						</AlertDialog>
					</div>
				</main>
				<CreateDatasetDialog
					open={createDatasetDialogOpen}
					onOpenChange={setCreateDatasetDialogOpen}
					onSubmit={handleCreateDataset}
				/>
				<CreateDerivativeDialog
					parentDataset={{
						id: dataset.id,
						name: dataset.name,
						dataset_kind: dataset.dataset_kind,
					}}
					open={showDerivativeDialog}
					onOpenChange={setShowDerivativeDialog}
					onSuccess={(datasetId) => navigate(`/dataset/${datasetId}`)}
				/>
			</div>
		</SidebarProvider>
	);
}
